import React from "react";
import { OfferPerPromoQuery } from "types/graphql-types";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import {
  ButtonsContainer,
  HeroBanner,
  HeroBannerAction,
  HeroBannerRightPanel,
  HeroBannerTitle,
  isSanityImagePresent,
  SectionContainer,
  TextStyle
} from "@silversea-ssc/ui-components-react";
import { offerTitle } from "./helpers";
import OfferHighlights from "components/OfferHighlights/OfferHighlights";
import FindYourCruise from "components/FindYourCruise";
import SanityBuilderToComponents, {
  SanityBuilderSectionType
} from "components/PageBuilder";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import { IntroButtons } from "./IntroButtons";
import { BlockContent } from "components/BlockContent";
import CallOurReservationTeam from "components/CallOurReservationTeam/CallOurReservationTeam";
import * as styles from "./OffersPerPromo.module.scss";
import { dateFromOptionalDate } from "src/utils/date";
import GenericBottomStickyBar from "components/GenericBottomStickyBar/GenericBottomStickyBar";
import { isPresent } from "src/utils/checks";
import { LocalizedPromo } from "./types";
import PageBuilderButtonCTA, {
  PageBuilderButtonCTAProps
} from "components/PageBuilder/PageBuilderButtonCTA";
import NativeHeroImage from "components/NativeHeroImage/NativeHeroImage";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

type NonNullableOfferPerPromoQuery = OfferPerPromoQuery & {
  offer: NonNullable<OfferPerPromoQuery["offer"]>;
} & { remoteOffer: LocalizedPromo };

const OfferPerPromoLoaded: React.FC<
  React.PropsWithChildren<NonNullableOfferPerPromoQuery>
> = ({
  offer,
  remoteOffer,
  firstLastDepartureCruiseDates,
  cruisePromosContent,
  shipsContent,
  cruisesTagContent,
  themedCruisesContent,
  raqPagePath
}) => {
  const { countryCode } = useGeolocation();
  const showDraftContent = isPresent(process.env.SANITY_API_TOKEN);
  const configurationCode = offer.configuration?.algoliaCode;
  const language = useLanguage();

  const {
    highlightsTitle,
    offerHighlights,
    termsAndConditionsFootnote,
    data: remoteData
  } = remoteOffer;

  const showDraftWarning = showDraftContent && !remoteData;

  const algoliaQuery = React.useMemo(() => {
    return new FYCQueryBuilder()
      .filterByPromoCodes([configurationCode || 0], countryCode)
      .generate();
  }, [configurationCode, countryCode]);

  const realTitle = offer.extendedTitle;

  const expireOn =
    remoteData?.toDate.type == "givenDate"
      ? dateFromOptionalDate(remoteData.toDate)
      : undefined;

  const filteredCTAButtons = offer.ctaButtons?.filter(isPresent) || [];
  const ctaButtonsOrDefault =
    filteredCTAButtons.length > 0
      ? filteredCTAButtons
      : [buildDefaultCallExpertCTA(), buildDefaultRAQCTA(raqPagePath)];

  const imageComponent = isSanityImagePresent(offer.heroImage) ? (
    <NativeHeroImage
      image={offer.heroImage}
      alt={offer.heroImage?.alt?.localized || offer.title?.localized || ""}
    />
  ) : undefined;

  const offerTitleComponent = offerTitle(
    language,
    realTitle,
    expireOn,
    remoteData?.tokenValues,
    offer.showCountdown,
    offer.countdownLabel?.localized
  );

  return (
    <React.Fragment>
      <HeroBanner
        backgroundImageComponent={imageComponent}
        loading={undefined}
        overlay="right"
      >
        <HeroBannerRightPanel>
          <HeroBannerTitle>{offer.title?.localized}</HeroBannerTitle>

          {ctaButtonsOrDefault && (
            <HeroBannerAction>
              <ButtonsContainer align="left" mobileFullSize>
                {ctaButtonsOrDefault.map((b) => (
                  <PageBuilderButtonCTA
                    key={b?._key}
                    forHero="inverted"
                    {...b}
                  />
                ))}
              </ButtonsContainer>
            </HeroBannerAction>
          )}
        </HeroBannerRightPanel>
      </HeroBanner>
      <SectionContainer
        spacingTop="regular"
        spacingBottom={null}
        dataContentName="offer_title"
      >
        {showDraftWarning && (
          <TextStyle fontSize="large" textAlign="center" boxed>
            Warning: no data for this promo. <br />
            Check the configuration and be sure that is published and
            transformed.
            <br />
          </TextStyle>
        )}
        {offerTitleComponent}
      </SectionContainer>
      <SectionContainer
        spacingTop="compact"
        spacingBottom={null}
        dataContentName="offer_highlights"
      >
        <OfferHighlights
          variant="large"
          title={highlightsTitle?.localized}
          offers={offerHighlights}
          tokenValues={remoteData?.tokenValues}
          defaultOfferIconUrl={offer.iconVertical?.asset?.url}
        />
      </SectionContainer>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        dataContentName="offer_details"
      >
        <React.Fragment>
          <TextStyle fontSize="large" textAlign="center" boxed>
            <BlockContent
              value={offer.description?.localized}
              tokens={remoteData?.tokenValues}
            />
          </TextStyle>

          {termsAndConditionsFootnote &&
            termsAndConditionsFootnote.localized && (
              <TextStyle fontSize="disclaimer" textAlign="center" boxed>
                <BlockContent
                  value={termsAndConditionsFootnote?.localized}
                  tokens={remoteData?.tokenValues}
                />
              </TextStyle>
            )}
        </React.Fragment>

        <IntroButtons raqURL={raqPagePath} offer={offer} />

        <CallOurReservationTeam
          className={styles.callReservationTeam}
          prefixOr
        />
      </SectionContainer>
      {offer.sections && offer.sections?.length > 0 && (
        <SanityBuilderToComponents
          sections={offer.sections as SanityBuilderSectionType[]}
          initialBackgroundColor="neutral05"
        />
      )}
      <FindYourCruise
        id="fyc"
        showTitle={false}
        backgroundColor="neutral10"
        initialSearchQuery={algoliaQuery}
        resultsPageSize={9}
        showExpeditionFilter={true}
        specialTypeFilter={undefined}
        showDestinationFilter={true}
        showShipFilter={true}
        showDurationFilter={true}
        showPromosFilter={true}
        showFeaturesFilter={true}
        showCruiseEventsFilter={true}
        firstLastDepartureCruiseDates={firstLastDepartureCruiseDates}
        promosContent={cruisePromosContent}
        cruisesTagContent={cruisesTagContent}
        excludePromosByConfigurationCodes={
          configurationCode ? [configurationCode] : []
        }
        shipsContent={shipsContent}
        themedCruisesContent={themedCruisesContent}
      />
      {offer.sectionsBottom && offer.sectionsBottom?.length > 0 && (
        <SanityBuilderToComponents
          sections={offer.sectionsBottom as SanityBuilderSectionType[]}
          initialBackgroundColor="white"
        />
      )}

      {offer.stickyEnabled && (
        <GenericBottomStickyBar
          title={offer.stickyTitle?.localized}
          content={offer.stickyContent?.localized}
          ctaButton={offer.stickyCTA?.filter(isPresent)?.[0]}
        />
      )}
    </React.Fragment>
  );
};

const buildDefaultRAQCTA = (
  raqPagePath: string | undefined
): PageBuilderButtonCTAProps => ({
  _key: "default",
  defaultFullPath: raqPagePath,
  __typename: "SanityRaqCTA" as const,
  variant: "primary" as const
});

const buildDefaultCallExpertCTA = (): PageBuilderButtonCTAProps => ({
  _key: "default",
  __typename: "SanityPhoneNumberCTA" as const,
  variant: "secondary" as const
});

export default React.memo(OfferPerPromoLoaded);
